import { Link } from 'react-router-dom';
import { Factory } from 'lucide-react';

function IndustrialManufacturing() {
  return (
    <div className="pt-28">
      {/* Hero Section with Background Image */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1565793298595-6a879b1d9492?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
        ></div>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Factory className="w-16 h-16 text-gold mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Industrial & <span className="text-gold">Manufacturing</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Warehouses, distribution centers, production facilities, and industrial complexes
          </p>
        </div>
      </section>

      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              Williams Diversified delivers robust construction solutions for industrial and manufacturing facilities where structural integrity, operational efficiency, and rapid project delivery are critical. We build spaces engineered for heavy equipment, high traffic, and demanding production environments.
            </p>
            <p>
              Our expertise spans warehouse construction, distribution centers, manufacturing plants, and industrial support facilities. We understand the specialized requirements of industrial projects—from reinforced concrete floors and crane systems to specialized HVAC and power distribution infrastructure.
            </p>
            
            <h3 className="text-3xl font-bold text-white mt-12 mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>Our Industrial & Manufacturing Capabilities</h3>
            <ul className="space-y-4">
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Warehouse and distribution center construction</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Manufacturing facility buildouts</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Cold storage and temperature-controlled facilities</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Heavy equipment foundations and crane systems</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Industrial HVAC and electrical systems</span></li>
              <li className="flex items-start"><span className="text-gold mr-3">•</span><span>Loading dock and logistics infrastructure</span></li>
            </ul>

            <div className="bg-black border border-gold/30 rounded-lg p-8 mt-12">
              <h4 className="text-2xl font-bold text-gold mb-4">Built for Operational Excellence</h4>
              <p className="text-gray-300 leading-relaxed">
                Our industrial construction projects are designed and built to optimize your operations. We work closely with operations teams, engineers, and equipment manufacturers to deliver facilities that maximize efficiency, safety, and long-term durability in the most demanding environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Maintenance Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-8 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Industrial <span className="text-gold">Facility Maintenance</span>
          </h3>
          <p className="text-gray-300 text-lg leading-relaxed text-center mb-8">
            Keep your industrial operations running smoothly with our comprehensive maintenance services designed for manufacturing and warehouse environments.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">Preventive Maintenance</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Heavy equipment foundation inspections</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Industrial HVAC system maintenance</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Loading dock door & leveler service</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Crane & overhead system inspections</li>
              </ul>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6">
              <h4 className="text-gold font-bold text-xl mb-3">24/7 Emergency Response</h4>
              <ul className="text-gray-300 space-y-2">
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Production line infrastructure repairs</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Electrical system emergencies</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Roof & structural damage response</li>
                <li className="flex items-start"><span className="text-gold mr-2">•</span>Plumbing & drainage repairs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* National Rollout Programs Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            National <span className="text-gold">Rollout Programs</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">
            Scalable construction solutions for industrial and manufacturing companies requiring consistent quality across multiple facilities
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">50+</div>
              <div className="text-gray-300 font-medium mb-2">States Covered</div>
              <p className="text-gray-500 text-sm">Nationwide service capability with local expertise</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">1</div>
              <div className="text-gray-300 font-medium mb-2">Point of Contact</div>
              <p className="text-gray-500 text-sm">Single account manager for all facilities</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">100%</div>
              <div className="text-gray-300 font-medium mb-2">Quality Consistency</div>
              <p className="text-gray-500 text-sm">Standardized execution across every facility</p>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center hover:border-gold/40 transition-all">
              <div className="text-4xl font-bold text-gold mb-2">24/7</div>
              <div className="text-gray-300 font-medium mb-2">Support</div>
              <p className="text-gray-500 text-sm">Round-the-clock emergency response</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-4 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Why Choose Us for <span className="text-gold">Industrial & Manufacturing</span>
          </h3>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Building facilities engineered for operational excellence
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">01</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Production-Ready Design</h4>
                <p className="text-gray-400 text-sm mb-4">We understand workflow and operational efficiency. Facilities designed around your production processes, not the other way around.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">02</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Heavy Industrial Expertise</h4>
                <p className="text-gray-400 text-sm mb-4">Specialized in heavy equipment foundations, crane systems, reinforced floors, and high-capacity electrical distribution.</p>
              </div>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all">
              <div className="h-48 bg-gradient-to-br from-gold/20 to-black flex items-center justify-center">
                <span className="text-gold text-5xl font-bold">03</span>
              </div>
              <div className="p-6">
                <h4 className="text-gold font-bold text-lg mb-2">Expansion Without Shutdown</h4>
                <p className="text-gray-400 text-sm mb-4">Strategic phasing to keep your operations running during construction. We work around your production schedule, not against it.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-12 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Our Industrial <span className="text-gold">Construction Process</span>
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">1</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Operations Review</h4>
              <p className="text-gray-400 text-sm">Understanding your workflow, equipment needs, and production requirements for optimal facility design.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">2</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Phased Planning</h4>
              <p className="text-gray-400 text-sm">Strategic scheduling to maintain production during construction with minimal operational impact.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">3</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Heavy Construction</h4>
              <p className="text-gray-400 text-sm">Foundations, structural steel, specialized MEP systems, and equipment installation coordination.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">4</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Operational Handoff</h4>
              <p className="text-gray-400 text-sm">Systems commissioning, equipment startup support, and training for your operations team.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Engineered for <span className="text-gold">Performance</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Partner with a contractor who understands industrial requirements
          </p>
          <Link to="/contact" className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}

export default IndustrialManufacturing;